import type * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import type * as chartJs from 'chart.js'

import * as color from '../../../../../color'
import * as utils from '../utils'

/**
 * Transform data from
 * {@link ents.core.display.Chart.Options<ents.core.display.chart.Type.ScatterChart>} to
 * {@link chartJs.ChartOptions<'scatter'>}
 */
export function getOptions({
  config,
  data,
}: ents.core.display.chart.Chart<ents.core.display.chart.Type.Scatter>): chartJs.ChartOptions<'scatter'> {
  let options: chartJs.ChartOptions<'scatter'> = {
    ...utils.DEFAULT_OPTIONS,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function (value) {
            if (TypeChecker.isNumber(value) && value >= 1000000) {
              return value / 1000000 + 'M'
            }
            return value
          },
        },
      },
    },
  }
  /**
   * Will be equal to {@link config?.name} if available; otherwise, it defaults to
   * {@link data.name}. If neither is defined, `undefined` is assigned.
   */
  const chartTitle = config?.name ?? data?.name
  if (chartTitle) {
    options = {
      ...options,
      plugins: {
        ...options.plugins,
        title: {
          align: 'center',
          color: color.BLACK,
          display: true,
          font: {
            weight: 'bold',
          },
          padding: {
            bottom: 20,
          },
          text: chartTitle,
        },
      },
    }
  }
  if (data) {
    options.plugins = {
      ...options.plugins,
      tooltip: {
        callbacks: {
          label: item => _getScatterPointLabelByIndex(data, item),
        },
        displayColors: data?.values
          ? utils.getUniqueDatasetNames(data.values).size > 1
          : false,
      },
    }

    function _addAxisLabel(axis: 'x' | 'y', label: string) {
      return (options.scales = {
        ...options.scales,
        [axis]: {
          ...options.scales?.[axis],
          title: {
            display: true,
            font: {
              weight: 'bold',
            },
            text: label,
          },
        },
      })
    }

    if (data.xLabel) {
      _addAxisLabel('x', data.xLabel)
    }

    if (data.yLabel) {
      _addAxisLabel('y', data.yLabel)
    }

    if (config) {
      const { onItemClick } = config

      if (onItemClick) {
        options = {
          ...options,
          onClick(_, elements) {
            const point = utils.getPointByIndex(data, elements)
            if (point) {
              onItemClick(point)
            }
          },
          onHover: (_, elements, chart) => {
            // Change cursor to pointer if hovering over a scatter chart point
            chart.canvas.style.cursor = elements.length ? 'pointer' : 'default'
          },
        }
      }
    }
  }

  return options
}

/**
 * Returns a label for a scatter chart point based on the point's index
 */
function _getScatterPointLabelByIndex(
  data: ents.core.display.chart.data.Of<ents.core.display.chart.Type.Scatter>,
  item: chartJs.TooltipItem<'scatter'>,
): string {
  const itemDatasetIndex: number | undefined = item.datasetIndex
  const itemIndex: number | undefined = item.dataIndex
  return data.values?.[itemDatasetIndex]?.points?.[itemIndex].name ?? ''
}
